<template>
  <div class="v2-my-enrollments">
    <div class="v2-my-enrollments__toolbar">
      <div v-if="categoryOptions.length" class="v2-my-enrollments__category">
        <BaseSelect
          v-model="categoryFilter"
          :options="categorySelectOptions"
          placeholder="Todas las categorías"
        />
      </div>
    </div>

    <section class="v2-my-enrollments__tabs" role="tablist" aria-label="Filtrar inscripciones">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="v2-my-enrollments__tab"
        :class="{ 'v2-my-enrollments__tab--active': activeTab === tab.value }"
        :aria-selected="activeTab === tab.value"
        @click="activeTab = tab.value"
      >
        <v-icon size="16">{{ tab.icon }}</v-icon>
        <span>{{ tab.label }}</span>
        <span v-if="tab.count > 0" class="v2-my-enrollments__tab-count">{{ tab.count }}</span>
      </button>
    </section>

    <div v-if="loadingMyEnrollments" class="v2-my-enrollments__loading">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando tus inscripciones…</p>
    </div>

    <div v-else-if="visibleEnrollments.length === 0" class="v2-my-enrollments__empty">
      <EmptyState
        :title="emptyState.title"
        :description="emptyState.description"
        :icon="emptyState.icon"
      >
        <BaseButton
          v-if="hasActiveCategoryFilter"
          variant="primary"
          @click="categoryFilter = ''"
        >
          Quitar filtro
        </BaseButton>
        <BaseButton
          v-else
          variant="primary"
          icon="mdi-calendar-search"
          @click="$emit('explore')"
        >
          Explorar eventos
        </BaseButton>
      </EmptyState>
    </div>

    <ul v-else class="v2-my-enrollments__grid">
      <li
        v-for="item in visibleEnrollments"
        :key="getEventId(item)"
        class="v2-my-enrollments__grid-item"
      >
        <EnrollmentCard :item="item" />
      </li>
    </ul>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import BaseSelect from "@/components/memberDashboardV2/ui/BaseSelect.vue";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import EnrollmentCard from "./EnrollmentCard.vue";
import {
  groupEnrollmentsByStatus,
  sortEnrollmentsByDate,
  extractEnrollmentCategories,
  getEventId,
} from "./enrollmentHelpers";

export default {
  name: "MyEnrollments",
  components: { BaseButton, BaseSelect, EmptyState, EnrollmentCard },
  emits: ["explore"],
  data() {
    return {
      activeTab: "upcoming",
      loadingMyEnrollments: false,
      purchasedEvents: [],
      categoryFilter: "",
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    groupedEnrollments() {
      return groupEnrollmentsByStatus(this.purchasedEvents);
    },
    upcomingEnrollments() {
      return sortEnrollmentsByDate(
        [...this.groupedEnrollments.live, ...this.groupedEnrollments.upcoming],
        "asc"
      );
    },
    pastEnrollments() {
      return sortEnrollmentsByDate(this.groupedEnrollments.past, "desc");
    },
    categoryOptions() {
      return extractEnrollmentCategories(this.purchasedEvents);
    },
    categorySelectOptions() {
      return [
        { value: "", label: "Todas las categorías" },
        ...this.categoryOptions.map((c) => ({ value: c, label: c })),
      ];
    },
    visibleEnrollments() {
      const source = this.activeTab === "upcoming" ? this.upcomingEnrollments : this.pastEnrollments;
      if (!this.categoryFilter) return source;
      return source.filter((it) => {
        const ev = it.event || it;
        return ev && ev.category === this.categoryFilter;
      });
    },
    tabs() {
      return [
        { value: "upcoming", label: "Próximos", icon: "mdi-calendar-clock", count: this.upcomingEnrollments.length },
        { value: "past", label: "Pasados", icon: "mdi-calendar-check", count: this.pastEnrollments.length },
      ];
    },
    hasActiveCategoryFilter() {
      return Boolean(this.categoryFilter);
    },
    emptyState() {
      if (this.hasActiveCategoryFilter) {
        return {
          title: "Sin resultados",
          description: "No tienes inscripciones en esta categoría. Prueba otra o explora el catálogo.",
          icon: "mdi-filter-off-outline",
        };
      }
      if (this.activeTab === "upcoming") {
        return {
          title: "Sin eventos próximos",
          description: "Cuando te inscribas a un evento aparecerá aquí. Explora el catálogo y elige uno.",
          icon: "mdi-calendar-clock",
        };
      }
      return {
        title: "Aún no has asistido a eventos",
        description: "Tu historial de eventos pasados aparecerá en esta sección cuando asistas al primero.",
        icon: "mdi-calendar-check",
      };
    },
  },
  methods: {
    ...mapMutations("loader", ["loading", "loaded"]),
    ...mapMutations("notification", ["show"]),
    getEventId,
    async loadPurchasedEvents() {
      this.loadingMyEnrollments = true;
      try {
        const response = await Vue.axios({
          method: "GET",
          url: "/items/member/me/purchased-events",
        });
        const items = response && response.data && response.data.items;
        this.purchasedEvents = Array.isArray(items) ? items : [];
      } catch (e) {
        this.purchasedEvents = [];
        this.show({
          text: "No se pudieron cargar tus inscripciones. Intenta más tarde.",
          color: "error",
        });
      } finally {
        this.loadingMyEnrollments = false;
      }
    },
  },
  async mounted() {
    this.loading();
    try {
      await this.loadPurchasedEvents();
    } finally {
      this.loaded();
    }
  },
};
</script>

<style scoped>
.v2-my-enrollments {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.v2-my-enrollments__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.v2-my-enrollments__category {
  min-width: 240px;
}

.v2-my-enrollments__tabs {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  align-self: flex-start;
}

.v2-my-enrollments__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-4);
  background: none;
  border: 0;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-muted);
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.v2-my-enrollments__tab:hover {
  color: var(--color-text);
}

.v2-my-enrollments__tab--active {
  background-color: var(--color-primary);
  color: var(--color-primary-on);
}

.v2-my-enrollments__tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: var(--color-surface-low);
  color: var(--color-text);
  font-size: 11px;
  font-weight: var(--fw-bold);
  border-radius: var(--radius-full);
}

.v2-my-enrollments__tab--active .v2-my-enrollments__tab-count {
  background-color: var(--color-amber);
  color: var(--color-amber-on);
}

.v2-my-enrollments__loading,
.v2-my-enrollments__empty {
  padding: var(--space-8);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.v2-my-enrollments__loading {
  gap: var(--space-2);
}

.v2-my-enrollments__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 600px) {
  .v2-my-enrollments__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .v2-my-enrollments__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.v2-my-enrollments__grid-item {
  display: flex;
  min-width: 0;
}

.v2-my-enrollments__grid-item > * {
  flex: 1 1 auto;
  width: 100%;
}

@media (max-width: 600px) {
  .v2-my-enrollments__category {
    min-width: 0;
    width: 100%;
  }
}
</style>
